//TODO Use an actual component for cards (I just copied and pasted for now...)

import { useMemo, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { toast } from "react-toastify";

import Table from "../shared/components/Table";
import useTable from "../shared/hooks/useTable";
import Badge from "../shared/components/Badge";
import useCheckboxColumn from "../shared/components/Table/UseCheckboxColumn";
import useActionsColumn from "../shared/components/Table/UseActionsColumn";
import useDateColumn from "../shared/hooks/useDateColumn";
import "../shared/style/typography.scss";

import Heading from "./Typography/Heading";
import CopyButton from "./UserInput/Buttons/CopyButton";
import appApi from "../api";
import CreateOrUpdateSurveyCollectorDialog from "./Dialogs/CreateOrUpdateSurveyCollectorDialog";

const columnHelper = createColumnHelper();

/**
 * @param {object} props
 * @param {object[]} props.collectors
 */
function SurveyCollectors({ collectors, setCollectors }) {
    const [isUpdateCollectorDialogOpen, setIsUpdateCollectorDialogOpen] = useState(false);
    const [collectorToUpdate, setCollectorToUpdate] = useState(null);

    /** @type {[import("@tanstack/react-table").RowSelectionState, React.Dispatch<React.SetStateAction<import("@tanstack/react-table").RowSelectionState>>]} */
    const [rowSelection, setRowSelection] = useState({});
    const checkboxColumn = useCheckboxColumn();
    const createdAtColumn = useDateColumn({
        id: "created_at",
        header: "Date",
        width: "20%",
    });

    const actionsColumn = useActionsColumn(
        [
            {
                label: "Open",
                onClick: (row) => toggleIsActiveForSelectedLinks(row, true),
            },
            {
                label: "Close",
                onClick: (row) => toggleIsActiveForSelectedLinks(row, false),
            },
            {
                label: "Edit",
                onClick: (row) => onEdit(row),
            },
            {
                label: "Delete",
                onClick: (row) => deleteSelectedCollectors(row),
            },
        ],
        "7.5%",
    );

    const table = useTable({
        data: useMemo(
            () =>
                (collectors?.map ? collectors : []).map((row) => ({
                    ...row,
                    link: `${window.location.origin}/survey/${row.link_key}`,
                    created_at: new Date(row.created_at),
                })),
            [collectors],
        ),
        columns: useMemo(
            () => [
                checkboxColumn,

                columnHelper.accessor("link_name", {
                    header: "Collector",
                    width: "25%",
                    cell: (info) => (
                        <div style={{ display: "flex", flexDirection: "column" }}>
                            <span className="paragraph-4-semibold" style={{ color: "var(--colors-primary-slate-500)" }}>
                                {info?.getValue()}
                            </span>
                            <div
                                title={info?.row.original.link}
                                className="paragraph-6-regular"
                                style={{
                                    color: "var(--colors-primary-slate-500)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {info?.row.original.link}
                            </div>
                        </div>
                    ),
                }),
                columnHelper.display({
                    id: "copy-button",
                    width: "7.5%",
                    cell: (info) => (
                        <div>
                            <CopyButton textToCopy={info?.row.original.link} />
                        </div>
                    ),
                }),
                columnHelper.accessor("response_count", {
                    header: "Responses",
                    width: "15%",
                    cell: (info) => (
                        <div className="paragraph-5-regular" style={{ color: "var(--colors-primary-slate-500)" }}>
                            {info?.getValue()}
                        </div>
                    ),
                }),
                columnHelper.accessor("booked_appointment_count", {
                    header: "Appointments",
                    width: "15%",
                    cell: (info) => (
                        <div className="paragraph-5-regular" style={{ color: "var(--colors-primary-slate-500)" }}>
                            {info?.getValue()}
                        </div>
                    ),
                }),

                createdAtColumn,

                columnHelper.accessor("is_active", {
                    header: "Status",
                    width: "10%",
                    cell: (info) =>
                        info?.getValue() ? (
                            <Badge text="Open" className="badge-success badge-filled badge-small" leftIcon={false} />
                        ) : (
                            <Badge text="Closed" className="badge-neutral badge-filled badge-small" leftIcon={false} />
                        ),
                }),
                actionsColumn,
            ],
            [checkboxColumn, actionsColumn, createdAtColumn],
        ),
        rowSelectionState: {
            rowSelection,
            setRowSelection,
        },
        paginationOptions: {
            defaultPageSize: 5,
            disableChangePageSize: true,
            disableGoToPage: true,
        },
        defaultSort: [
            {
                id: "created_at",
                desc: true,
            },
        ],
    });

    function parseSelectedRows(rowSelection, clickedRow) {
        let selectedRows = null;

        const selectedRowIndexes = Object.keys(rowSelection);
        if (selectedRowIndexes.length === 0) {
            selectedRows = [clickedRow.id];
        } else {
            selectedRows = collectors
                .filter((_, index) => selectedRowIndexes.includes(index.toString()))
                .map((collector) => collector.id);
        }

        return selectedRows;
    }

    /**
     * @param {object} row
     * @param {boolean} isActive
     */
    async function toggleIsActiveForSelectedLinks(row, isActive) {
        let collectorsToUpdate = [];

        try {
            const selectedRows = parseSelectedRows(rowSelection, row);

            const newCollectorsData = collectors.map((collector) => {
                return selectedRows.includes(collector.id) ? { ...collector, is_active: isActive } : collector;
            });

            collectorsToUpdate = newCollectorsData.filter((collector) => selectedRows.includes(collector.id));
            await appApi.patch(
                "/company/survey-links",
                collectorsToUpdate.map((collector) => ({
                    id: collector.id,
                    is_active: isActive,
                })),
            );

            setCollectors(newCollectorsData);
            setRowSelection({});
        } catch (error) {
            toast.error(
                `Failed to update collector status${collectorsToUpdate.length > 1 ? ". No collectors have been updated." : ""}`,
            );
        }
    }

    /** @param {object} row */
    async function deleteSelectedCollectors(row) {
        let collectorsToUpdate = [];

        try {
            const selectedRows = parseSelectedRows(rowSelection, row);

            const newCollectors = collectors.map((collector) => {
                return selectedRows.includes(collector.id) ? { ...collector, is_archived: true } : collector;
            });

            collectorsToUpdate = newCollectors.filter((collector) => selectedRows.includes(collector.id));
            await appApi.patch(
                "/company/survey-links",
                collectorsToUpdate.map((collector) => ({
                    id: collector.id,
                    is_archived: collector.is_archived,
                })),
            );

            setCollectors((prev) => prev.filter((item) => !selectedRows.includes(item.id)));
            setRowSelection({});
        } catch (error) {
            toast.error(
                `Failed to delete collector${collectorsToUpdate.length > 1 ? ". No collectors have been deleted." : ""}`,
            );
        }
    }

    function onEdit(selectedCollector) {
        setIsUpdateCollectorDialogOpen(true);
        setCollectorToUpdate(selectedCollector);
    }

    return (
        <div>
            <Heading style={{ marginBottom: "16px" }}>Survey collectors</Heading>

            <Table table={table} noDataComponent="No surveys available" />

            <CreateOrUpdateSurveyCollectorDialog
                title="Edit collector"
                isOpen={isUpdateCollectorDialogOpen}
                collectorToUpdate={collectorToUpdate}
                onCollectorUpdated={(updatedCollector) => {
                    setCollectors(
                        collectors.map((collector) =>
                            collector.id === updatedCollector.id ? updatedCollector : collector,
                        ),
                    );

                    toast.success("Survey link updated");
                }}
                onClose={() => {
                    setIsUpdateCollectorDialogOpen(false);
                    setCollectorToUpdate(null);
                }}
                saveButtonText="Update"
            />
        </div>
    );
}

export default SurveyCollectors;
